import React from 'react';
import { FTANode, FTANodeType } from '../../types/fta.types';
import { Calendar, User, ClipboardList } from 'lucide-react';
import clsx from 'clsx';

interface ActionPlanTableProps {
  nodes: FTANode[];
  onUpdateNode: (id: string, data: any) => void;
}

export const ActionPlanTable: React.FC<ActionPlanTableProps> = ({ nodes, onUpdateNode }) => {
  // Sort nodes: Top event first, then others, generally keeping tree order if possible or just type priority
  const sortedNodes = [...nodes].sort((a, b) => {
    // Priority: Top Event -> Gates -> Events
    const score = (type: FTANodeType) => {
      switch (type) {
        case FTANodeType.TOP_EVENT: return 0;
        case FTANodeType.INTERMEDIATE_EVENT: return 1;
        case FTANodeType.AND_GATE: return 2;
        case FTANodeType.OR_GATE: return 2;
        case FTANodeType.BASIC_EVENT: return 3;
        case FTANodeType.UNDEVELOPED_EVENT: return 3;
        default: return 4;
      }
    };
    return score(a.data.type) - score(b.data.type);
  });

  const handleDataChange = (id: string, field: string, value: string) => {
    const node = nodes.find(n => n.id === id);
    if (!node) return;

    onUpdateNode(id, {
      ...node.data,
      [field]: value
    });
  };

  const handleMitigationChange = (id: string, field: string, value: string) => {
    const node = nodes.find(n => n.id === id);
    if (!node) return;

    const currentMitigation = node.data.mitigation || { action: '', resource: '', dueDate: '', status: 'Open' };

    onUpdateNode(id, {
      ...node.data,
      mitigation: {
        ...currentMitigation,
        [field]: value
      }
    });
  };

  return (
    <div className="w-full h-full overflow-auto bg-slate-50 dark:bg-slate-950 p-6 transition-colors duration-200">
      <div className="max-w-full mx-auto bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-700 rounded-lg flex flex-col h-full transition-colors">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 sticky top-0 z-10 rounded-t-lg">
          <h2 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <ClipboardList className="text-indigo-600 dark:text-indigo-400" size={20} />
            Mitigation & Action Plan
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Assign actions, resources, and deadlines to specific failure modes. You can also rename gates and events here.</p>
        </div>

        <div className="overflow-auto flex-1">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-4 py-3 font-medium w-1/5 min-w-[250px]">Node / Failure Mode</th>
                <th className="px-4 py-3 font-medium w-32">Type</th>
                <th className="px-4 py-3 font-medium w-24">Prob.</th>
                <th className="px-4 py-3 font-medium min-w-[200px]">Required Action</th>
                <th className="px-4 py-3 font-medium w-48">Resource / Owner</th>
                <th className="px-4 py-3 font-medium w-40">Due Date</th>
                <th className="px-4 py-3 font-medium w-32">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {sortedNodes.map((node) => (
                <tr key={node.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors group">
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100 align-top">
                    <div className="flex flex-col gap-1">
                      <input
                        type="text"
                        value={node.data.label}
                        onChange={(e) => handleDataChange(node.id, 'label', e.target.value)}
                        className="w-full bg-transparent border border-transparent hover:border-slate-300 dark:hover:border-slate-600 focus:bg-white dark:focus:bg-slate-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded px-1.5 py-0.5 text-sm font-bold text-slate-800 dark:text-slate-200 outline-none transition-all placeholder:text-slate-400"
                        placeholder="Node Name"
                      />
                      <textarea
                        rows={2}
                        value={node.data.description || ''}
                        onChange={(e) => handleDataChange(node.id, 'description', e.target.value)}
                        className="w-full bg-transparent border border-transparent hover:border-slate-300 dark:hover:border-slate-600 focus:bg-white dark:focus:bg-slate-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded px-1.5 py-0.5 text-xs text-slate-500 dark:text-slate-400 outline-none transition-all resize-none placeholder:text-slate-300 dark:placeholder:text-slate-600 font-normal"
                        placeholder="Add description..."
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    {(() => {
                      const nt = node.data.type;
                      let badgeClasses = "bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-600";

                      if (nt === FTANodeType.TOP_EVENT) {
                        badgeClasses = "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800";
                      } else if (nt === FTANodeType.BASIC_EVENT) {
                        badgeClasses = "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800";
                      } else if (nt === FTANodeType.AND_GATE || nt === FTANodeType.OR_GATE) {
                        badgeClasses = "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800";
                      } else if (nt === FTANodeType.INTERMEDIATE_EVENT) {
                        badgeClasses = "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600";
                      }

                      return (
                        <span className={clsx(
                          "inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border uppercase tracking-wide mt-1",
                          badgeClasses
                        )}>
                          {nt.replace('_', ' ')}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-400 align-top pt-3.5">
                    {node.data.calculatedProbability
                      ? node.data.calculatedProbability.toExponential(2)
                      : (node.data.probability ? node.data.probability.toExponential(2) : '-')}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <textarea
                      rows={2}
                      className="w-full bg-slate-50 dark:bg-slate-700 border border-transparent focus:bg-white dark:focus:bg-slate-600 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-500/10 rounded px-2 py-1.5 text-sm text-slate-900 dark:text-slate-100 outline-none transition-all resize-none placeholder:text-slate-400"
                      placeholder="Describe mitigation action..."
                      value={node.data.mitigation?.action || ''}
                      onChange={(e) => handleMitigationChange(node.id, 'action', e.target.value)}
                    />
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="relative">
                      <User className="absolute left-2.5 top-2.5 text-slate-400" size={14} />
                      <input
                        type="text"
                        className="w-full pl-8 bg-slate-50 dark:bg-slate-700 border border-transparent focus:bg-white dark:focus:bg-slate-600 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-500/10 rounded px-2 py-1.5 text-sm text-slate-900 dark:text-slate-100 outline-none transition-all placeholder:text-slate-400"
                        placeholder="Unassigned"
                        value={node.data.mitigation?.resource || ''}
                        onChange={(e) => handleMitigationChange(node.id, 'resource', e.target.value)}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="relative">
                      <Calendar className="absolute left-2.5 top-2.5 text-slate-400" size={14} />
                      <input
                        type="date"
                        className="w-full pl-8 bg-slate-50 dark:bg-slate-700 border border-transparent focus:bg-white dark:focus:bg-slate-600 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-500/10 rounded px-2 py-1.5 text-sm text-slate-600 dark:text-slate-300 outline-none transition-all"
                        value={node.data.mitigation?.dueDate || ''}
                        onChange={(e) => handleMitigationChange(node.id, 'dueDate', e.target.value)}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <select
                      className={clsx(
                        "w-full rounded px-2 py-1.5 text-sm outline-none border border-transparent focus:ring-2 focus:ring-indigo-500/10 cursor-pointer appearance-none font-medium",
                        (node.data.mitigation?.status === 'Done') ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800" :
                          (node.data.mitigation?.status === 'In Progress') ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800" :
                            "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-600"
                      )}
                      value={node.data.mitigation?.status || 'Open'}
                      onChange={(e) => handleMitigationChange(node.id, 'status', e.target.value)}
                    >
                      <option value="Open">Open</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Done">Done</option>
                    </select>
                  </td>
                </tr>
              ))}
              {sortedNodes.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-400 dark:text-slate-500">
                    No nodes available. Create a diagram first.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
